import React, { useEffect, useState } from "react";

import {
  Autocomplete,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  SnackbarCloseReason
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Controller, useForm } from "react-hook-form";

import { methods } from "api/methods";
import { Ingredient, IngredientTypeKind } from "api/models";
import { resources } from "api/resources";
import { useApi } from "api/useApi";
import {
  Header2,
  InputLabel,
  PrimaryButton,
  TextField
} from "components/common";
import ConfirmationModal from "components/common/ConfirmationModal";
import { TranslationProvider } from "components/useTranslations";

import useIngredientsDetailsFormStyles from "./useIngredientsDetailsFormStyles";

export type IngredientsDetailsFormProps = {
  ingredient?: Ingredient;
  onNameSaved?: (name: string) => void;
};

enum IngredientFormFields {
  ingredientName = "name",
  ingredientCode = "code",
  ingredientRCode = "rCode",
  ingredientType = "ingredientTypeDisplayName",
  ingredientSegment = "selectedSegment",
  ingredientFamily = "selectedFamily",
  ingredientClass = "selectedClass"
}

const IngredientsDetailsForm: React.FC<IngredientsDetailsFormProps> = props => {
  const styles = useIngredientsDetailsFormStyles();
  const execute = useApi();
  const { ingredient, onNameSaved } = props;
  const [editIngredient, setEditIngredient] = useState<Ingredient>(
    ingredient as Ingredient
  );
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);

  // State whether form successful
  const [success, setSuccess] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // State whether form busy
  const [busy, setBusy] = useState(false);

  const [familyOptions, setFamilyOptions] = useState(
    [] as {
      name: string;
      keyValue: string;
      requiresClass?: boolean;
      classes: {
        name: string;
        keyValue: string;
      }[];
    }[]
  );

  const [classOptions, setClassOptions] = useState(
    [] as {
      name: string;
      keyValue: string;
    }[]
  );

  const getDefaultValues = () => {
    let defaultValue: Ingredient;
    if (editIngredient?.key && editIngredient) {
      defaultValue = {
        key: editIngredient?.key ?? "",
        name: editIngredient?.name ?? "",
        code: editIngredient?.code ?? "",
        rCode: editIngredient?.rCode ?? "",
        ingredientTypeDisplayName:
          editIngredient?.ingredientTypeDisplayName ?? "",
        selectedSegment: editIngredient?.selectedSegment ?? "",
        selectedFamily: editIngredient?.selectedFamily ?? "",
        selectedClass: editIngredient?.selectedClass ?? ""
      };
    } else {
      defaultValue = {
        key: "",
        name: "",
        code: "",
        rCode: "",
        ingredientTypeDisplayName:
          editIngredient?.ingredientTypeOptions?.find(
            // key is serialized as the enum name (e.g. "RawMaterial"), not its numeric value
            o =>
              (o.key as unknown as string) ===
              IngredientTypeKind[IngredientTypeKind.RawMaterial]
          )?.displayName ?? "",
        selectedSegment: "",
        selectedFamily: "",
        selectedClass: ""
      };
    }
    return defaultValue;
  };

  // React hook forms
  const {
    control,
    getValues,
    resetField,
    formState: { errors, isDirty },
    trigger,
    reset
  } = useForm<Ingredient>({
    defaultValues: getDefaultValues()
  });

  // Retrieval of edit ingredient may take time: reset default values upon change.
  useEffect(() => {
    reset(getDefaultValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIngredient]);

  // Populate Family/Class options for the ingredient's current Segment/Family when entering edit mode
  useEffect(() => {
    const families = editIngredient?.selectedSegment
      ? (editIngredient.ingredientSetUpDetailsDropdownOptions?.segments?.find(
          s => s.name === editIngredient.selectedSegment
        )?.families ?? [])
      : [];
    setFamilyOptions(families);
    setClassOptions(
      families.find(f => f.name === editIngredient?.selectedFamily)?.classes ??
        []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editIngredient]);

  useEffect(() => {
    setEditIngredient(ingredient as Ingredient);
  }, [ingredient]);

  // Save Button
  const onClick = () => {
    clearValidationErrors();
    trigger()
      .then(isValid => {
        if (!isValid) {
          return;
        }
        if (editIngredient?.key) {
          if (!confirmationModalIsOpen) setConfirmationModalIsOpen(true);
        } else {
          void onSave();
        }
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  };

  const applyServerValidationErrors = (response: Ingredient) => {
    if (response.nameValidationMessage !== "") {
      setNameValidationErrorMessage(response.nameValidationMessage);
    }
    if (response.codeValidationMessage !== "") {
      setCodeValidationErrorMessage(response.codeValidationMessage);
    }
    if (response.rCodeValidationMessage !== "") {
      setRCodeValidationErrorMessage(response.rCodeValidationMessage);
    }
    if (response.ingredientTypeValidationMessage) {
      setIngredientTypeValidationErrorMessage(
        response.ingredientTypeValidationMessage
      );
    }
    if (response.segmentValidationMessage) {
      setSegmentValidationErrorMessage(response.segmentValidationMessage);
    }
    if (response.familyValidationMessage) {
      setFamilyValidationErrorMessage(response.familyValidationMessage);
    }
    if (response.classValidationMessage) {
      setClassValidationErrorMessage(response.classValidationMessage);
    }
  };

  const clearValidationErrors = () => {
    setNameValidationErrorMessage("");
    setCodeValidationErrorMessage("");
    setRCodeValidationErrorMessage("");
    setIngredientTypeValidationErrorMessage("");
    setSegmentValidationErrorMessage("");
    setFamilyValidationErrorMessage("");
    setClassValidationErrorMessage("");
  };

  // Save Action
  const onSave = () => {
    setSuccess(false);
    setSaveFailed(false);
    clearValidationErrors();
    return trigger()
      .then(isValid => {
        if (isValid) {
          const formValues: Ingredient = {
            key: editIngredient?.key ?? "",
            name: getValues("name"),
            code: getValues("code"),
            rCode: getValues("rCode"),
            ingredientTypeDisplayName: getValues("ingredientTypeDisplayName"),
            ingredientTypeOptions: editIngredient?.ingredientTypeOptions,
            selectedSegment: getValues("selectedSegment"),
            selectedFamily: getValues("selectedFamily"),
            selectedClass: getValues("selectedClass"),
            ingredientSetUpDetailsDropdownOptions:
              editIngredient?.ingredientSetUpDetailsDropdownOptions
          };
          const creating = !editIngredient?.key;
          setBusy(true);
          return execute(
            editIngredient?.key
              ? `${resources.ingredient(editIngredient?.key)}`
              : `${resources.ingredients()}/create`,
            editIngredient?.key ? methods.PUT : methods.POST,
            formValues
          ).then((response: Ingredient) => {
            if (response?.isInvalid) {
              setSuccess(false);
              applyServerValidationErrors(response);
              return;
            }
            setSuccess(true);
            if (creating) {
              const emptyValues = getDefaultValues();
              reset(emptyValues);
              setEditIngredient({
                ...editIngredient,
                ...emptyValues
              });
              setFamilyOptions([]);
              setClassOptions([]);
            } else {
              setEditIngredient({
                ...formValues,
                ...response,
                ingredientTypeOptions: editIngredient?.ingredientTypeOptions,
                ingredientSetUpDetailsDropdownOptions:
                  editIngredient?.ingredientSetUpDetailsDropdownOptions
              });
              onNameSaved?.(response.name ?? formValues.name);
            }
            clearValidationErrors();
          });
        }
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setSuccess(false);
        setSaveFailed(true);
      })
      .finally(() => setBusy(false));
  };

  const handleClose = (
    event: React.SyntheticEvent<any> | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setSaveFailed(false);
  };

  // Validation Message State
  const [nameValidationErrorMessage, setNameValidationErrorMessage] = useState<
    string | undefined
  >("");
  const [codeValidationErrorMessage, setCodeValidationErrorMessage] = useState<
    string | undefined
  >("");
  const [rCodeValidationErrorMessage, setRCodeValidationErrorMessage] =
    useState<string | undefined>("");
  const [
    ingredientTypeValidationErrorMessage,
    setIngredientTypeValidationErrorMessage
  ] = useState<string | undefined>("");
  const [segmentValidationErrorMessage, setSegmentValidationErrorMessage] =
    useState<string | undefined>("");
  const [familyValidationErrorMessage, setFamilyValidationErrorMessage] =
    useState<string | undefined>("");
  const [classValidationErrorMessage, setClassValidationErrorMessage] =
    useState<string | undefined>("");

  // Updating after selections
  const applySelectedSegment = (value: string | null) => {
    setFamilyOptions(
      editIngredient?.ingredientSetUpDetailsDropdownOptions?.segments?.find(
        s => s.name === value
      )?.families ?? []
    );
    resetField("selectedFamily", { defaultValue: "" });
    resetField("selectedClass", { defaultValue: "" });
    setFamilyValidationErrorMessage("");
    setClassValidationErrorMessage("");
    setClassOptions([]);
  };

  const applySelectedFamily = (value: string | null) => {
    const filteredClasses =
      familyOptions?.find(x => x.name === value)?.classes ?? [];
    setClassOptions(filteredClasses);
    resetField("selectedClass", {
      defaultValue: filteredClasses.length === 1 ? filteredClasses[0].name : ""
    });
    setClassValidationErrorMessage("");
  };

  return (
    <TranslationProvider translations={ingredient?.translations}>
      {t => (
        <div
          className={
            editIngredient?.key
              ? styles.masterContainerEdit
              : styles.masterContainerCreate
          }
        >
          <ConfirmationModal
            isOpen={confirmationModalIsOpen}
            setIsOpen={setConfirmationModalIsOpen}
            callback={onSave}
          />
          <Paper className={styles.pageContent}>
            <form className={styles.form}>
              {/* First Row: form heading */}
              <Grid container>
                <Grid size={{ xs: 12 }} className={styles.spaceChildrenApart}>
                  <Header2>{t`__INGREDIENT_DETAILS_SUBTITLE`}</Header2>
                  <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    open={success || saveFailed}
                    autoHideDuration={6000}
                    onClose={handleClose}
                  >
                    <Alert
                      elevation={6}
                      variant="filled"
                      onClose={handleClose}
                      severity={saveFailed ? "error" : "success"}
                    >
                      {saveFailed
                        ? t`__INGREDIENT_DETAILS_SAVE_FAILED`
                        : t`__INGREDIENT_DETAILS_SAVED`}
                    </Alert>
                  </Snackbar>
                  <PrimaryButton
                    onClick={onClick}
                    name="saveIngredient"
                    disabled={!isDirty || busy}
                  >
                    {t`__INGREDIENT_DETAILS_SAVE`}
                  </PrimaryButton>
                </Grid>
              </Grid>
              {/* Second Row: name, code */}
              <Grid className={styles.rowOfInputs} container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => {
                      const { ref, ...passProps } = field;
                      return (
                        <TextField
                          {...passProps}
                          className={styles.input}
                          size="small"
                          variant="outlined"
                          label={t`__INGREDIENT_DETAILS_NAME`}
                          error={!!errors.name || !!nameValidationErrorMessage}
                          helperText={
                            errors.name?.message ?? nameValidationErrorMessage
                          }
                          onBlur={() => {
                            trigger(IngredientFormFields.ingredientName).catch(
                              (err: any) => {
                                // eslint-disable-next-line no-console
                                console.error(err);
                              }
                            );
                          }}
                        />
                      );
                    }}
                    name={IngredientFormFields.ingredientName}
                    control={control}
                    rules={{
                      required: t`__INGREDIENT_DETAILS_NAME_REQUIRED`,
                      validate: value =>
                        !!value?.trim() || t`__INGREDIENT_DETAILS_NAME_REQUIRED`
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => {
                      const { ref, ...passProps } = field;
                      return (
                        <TextField
                          {...passProps}
                          className={styles.input}
                          size="small"
                          variant="outlined"
                          label={t`__INGREDIENT_DETAILS_CODE`}
                          error={!!errors.code || !!codeValidationErrorMessage}
                          helperText={
                            errors.code?.message ?? codeValidationErrorMessage
                          }
                          onBlur={() => {
                            trigger(IngredientFormFields.ingredientCode).catch(
                              (err: any) => {
                                // eslint-disable-next-line no-console
                                console.error(err);
                              }
                            );
                          }}
                        />
                      );
                    }}
                    name={IngredientFormFields.ingredientCode}
                    control={control}
                    rules={{
                      required: t`__INGREDIENT_DETAILS_CODE_REQUIRED`,
                      validate: value =>
                        !!value?.trim() || t`__INGREDIENT_DETAILS_CODE_REQUIRED`
                    }}
                  />
                </Grid>
              </Grid>
              {/* Third Row: type, Rcode */}
              <Grid className={styles.rowOfInputs} container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => {
                      const { ref, ...passProps } = field;
                      return (
                        <FormControl
                          variant="outlined"
                          fullWidth
                          error={
                            !!errors.ingredientTypeDisplayName ||
                            !!ingredientTypeValidationErrorMessage
                          }
                        >
                          <InputLabel
                            id="ingredient-type-label"
                            className={styles.label}
                          >
                            {t`__INGREDIENT_DETAILS_TYPE`}
                          </InputLabel>
                          <Select
                            {...passProps}
                            variant="outlined"
                            label={t`__INGREDIENT_DETAILS_TYPE`}
                            labelId="ingredient-type-label"
                            id="ingredient-type-select"
                            className={styles.dropdown}
                            onBlur={() => {
                              trigger(
                                IngredientFormFields.ingredientType
                              ).catch((err: any) => {
                                // eslint-disable-next-line no-console
                                console.error(err);
                              });
                            }}
                          >
                            {editIngredient?.ingredientTypeOptions?.map(c => (
                              <MenuItem value={c.displayName} key={c.key}>
                                {c.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {errors.ingredientTypeDisplayName?.message ??
                              ingredientTypeValidationErrorMessage}
                          </FormHelperText>
                        </FormControl>
                      );
                    }}
                    name={IngredientFormFields.ingredientType}
                    control={control}
                    rules={{
                      required: t`__INGREDIENT_DETAILS_TYPE_REQUIRED`
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => {
                      const { ref, ...passProps } = field;
                      return (
                        <TextField
                          {...passProps}
                          className={styles.input}
                          size="small"
                          variant="outlined"
                          label={t`__INGREDIENT_DETAILS_RCODE`}
                          error={
                            !!errors.rCode || !!rCodeValidationErrorMessage
                          }
                          helperText={
                            errors.rCode?.message ?? rCodeValidationErrorMessage
                          }
                          onBlur={() => {
                            trigger(IngredientFormFields.ingredientRCode).catch(
                              (err: any) => {
                                // eslint-disable-next-line no-console
                                console.error(err);
                              }
                            );
                          }}
                        />
                      );
                    }}
                    name={IngredientFormFields.ingredientRCode}
                    control={control}
                    rules={{
                      required: t`__INGREDIENT_DETAILS_RCODE_REQUIRED`,
                      validate: value =>
                        !!value?.trim() ||
                        t`__INGREDIENT_DETAILS_RCODE_REQUIRED`
                    }}
                  />
                </Grid>
              </Grid>
              {/* Forth Row: Segment, Family */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => {
                      return (
                        <Autocomplete
                          options={
                            editIngredient?.ingredientSetUpDetailsDropdownOptions?.segments.map(
                              option => option.name ?? ""
                            ) ?? []
                          }
                          value={field.value}
                          onChange={(e, value) => {
                            field.onChange(value);
                            applySelectedSegment(value);
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={t`__INGREDIENT_DETAILS_SEGMENT_PLACEHOLDER`}
                              size="small"
                              label={t`__INGREDIENT_DETAILS_SEGMENT`}
                              error={segmentValidationErrorMessage !== ""}
                              helperText={segmentValidationErrorMessage}
                            />
                          )}
                        />
                      );
                    }}
                    name={IngredientFormFields.ingredientSegment}
                    control={control}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => (
                      <Autocomplete
                        options={
                          familyOptions.map(option => option.name ?? "") ?? []
                        }
                        value={field.value ?? ""}
                        onChange={(e, value) => {
                          field.onChange(value);
                          applySelectedFamily(value);
                          setFamilyValidationErrorMessage("");
                          void trigger(IngredientFormFields.ingredientFamily);
                        }}
                        disabled={familyOptions.length === 0 && !field.value}
                        renderInput={params => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t`__INGREDIENT_DETAILS_FAMILY_PLACEHOLDER`}
                            size="small"
                            label={t`__INGREDIENT_DETAILS_FAMILY`}
                            error={
                              (!!errors?.selectedFamily &&
                                familyOptions.length > 0) ||
                              familyValidationErrorMessage !== ""
                            }
                            helperText={
                              familyOptions.length > 0
                                ? (errors?.selectedFamily?.message ??
                                  familyValidationErrorMessage)
                                : ""
                            }
                          />
                        )}
                      />
                    )}
                    name={IngredientFormFields.ingredientFamily}
                    control={control}
                    rules={{
                      required:
                        familyOptions.length > 0
                          ? t`__INGREDIENT_DETAILS_FAMILY_REQUIRED`
                          : false
                    }}
                  />
                </Grid>
              </Grid>
              {/* Fifth Row: Class */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    render={({ field }) => {
                      return (
                        <Autocomplete
                          options={
                            classOptions.map(option => option.name ?? "") ?? []
                          }
                          value={field.value}
                          onChange={(e, value) => {
                            field.onChange(value);
                            setClassValidationErrorMessage("");
                            void trigger(IngredientFormFields.ingredientClass);
                          }}
                          disabled={classOptions.length === 0 && !field.value}
                          renderInput={params => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={t`__INGREDIENT_DETAILS_CLASS_PLACEHOLDER`}
                              size="small"
                              label={t`__INGREDIENT_DETAILS_CLASS`}
                              error={
                                (!!errors?.selectedClass &&
                                  classOptions.length > 0) ||
                                classValidationErrorMessage !== ""
                              }
                              helperText={
                                classOptions.length > 0
                                  ? (errors?.selectedClass?.message ??
                                    classValidationErrorMessage)
                                  : ""
                              }
                            />
                          )}
                        />
                      );
                    }}
                    name={IngredientFormFields.ingredientClass}
                    control={control}
                    rules={{
                      validate: value =>
                        !familyOptions.find(
                          family => family.name === getValues("selectedFamily")
                        )?.requiresClass ||
                        !!value?.trim() ||
                        t`__INGREDIENT_DETAILS_CLASS_REQUIRED`
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </Paper>
        </div>
      )}
    </TranslationProvider>
  );
};

export default IngredientsDetailsForm;
